import React, { useEffect, useMemo, useState } from 'react';
import { viabilidadeAPI, creditoAPI } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Loader2, RefreshCw, AlertTriangle, Tag, Wifi, Tv, Phone, Smartphone, Sparkles, CreditCard, MousePointerClick } from 'lucide-react';
import { fmtDoc, situacao as situacaoCredito, liberaPlanos, ehAnaliseN2, AVISO_N2 } from '@/components/credito/CreditoResultado';
import { ConectorAviso } from '@/components/ConectorAviso';

const brl = (v) => (v == null ? '-' : Number(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }));
const ICONES = { HDTV: Tv, INTERNET: Wifi, FONE: Phone, CELULAR: Smartphone };
const TEC = { HFC: { label: 'Cabo', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' }, GPON: { label: 'Fibra', cls: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/40 dark:text-fuchsia-300' } };

// Tecnologias vendáveis no HP a partir dos serviços da viabilidade (PTV*/VIRTUA/VOIP = cabo HFC; GPON = fibra)
export const tecnologiasDoHp = (servicos) => {
  if (!servicos?.length) return null;
  const ok = (re) => servicos.some((s) => re.test(s.tipo || '') && s.tecnica && s.comercial);
  return { HFC: ok(/PTV|VIRTUA|VOIP|HFC|CABO/i), GPON: ok(/GPON|FIBRA/i) };
};

const Produto = ({ p, viavel, onEscolher }) => (
  <div className={`rounded-lg border p-2.5 text-xs space-y-1 bg-white dark:bg-gray-900 ${viavel === false ? 'opacity-50' : ''}`} data-testid={`plano-${p.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-1">
      <p className="font-semibold leading-tight">{p.nome}</p>
      <div className="flex items-center gap-1">
        {p.selo && <Badge variant="outline" className="h-5 text-[10px] gap-0.5"><Sparkles className="w-3 h-3" /> {p.selo}</Badge>}
        {TEC[p.tecnologia] && <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${TEC[p.tecnologia].cls}`}>{TEC[p.tecnologia].label}</span>}
      </div>
    </div>
    {(p.download || p.franquia || p.tecnologia_tv || p.faixa) && (
      <p className="text-gray-500">
        {p.download && <>↓ {p.download}{p.upload ? ` • ↑ ${p.upload}` : ''}{p.franquia ? ` • ${p.franquia}` : ''}</>}
        {!p.download && p.franquia && <>{p.franquia}</>}
        {p.tecnologia_tv && <>{p.download || p.franquia ? ' • ' : ''}{p.tecnologia_tv}</>}
        {p.faixa && <>{p.download || p.franquia || p.tecnologia_tv ? ' • ' : ''}{p.faixa}</>}
      </p>
    )}
    <div className="flex flex-wrap gap-x-3 gap-y-0.5">
      {p.precos.map((pr, i) => (
        <span key={i} className={pr.condicao === 'SINGLE' || i === 0 ? 'font-bold text-purple-700 dark:text-purple-300' : 'text-gray-600 dark:text-gray-300'}>
          {brl(pr.preco)}<span className="font-normal text-gray-500"> {pr.condicao === 'SINGLE' ? 'sozinho' : pr.condicao ? pr.condicao.toLowerCase() : 'em combo'}</span>
        </span>
      ))}
      {p.precos.length === 0 && <span className="text-gray-400">sem preço informado</span>}
    </div>
    {onEscolher && p.precos.length > 0 && (
      <div className="flex flex-wrap gap-1 pt-0.5">
        {p.precos.map((pr, i) => (
          <button key={i} type="button" onClick={() => onEscolher(p, pr)} disabled={viavel === false}
            className="inline-flex items-center gap-1 rounded-md bg-purple-600 hover:bg-purple-700 disabled:opacity-40 px-2 py-1 text-[10px] font-semibold text-white"
            data-testid={`plano-${p.id}-usar-${i}`}>
            <MousePointerClick className="w-3 h-3" /> Usar {brl(pr.preco)}{pr.condicao && pr.condicao !== 'SINGLE' ? ` (${pr.condicao.toLowerCase()})` : ''}
          </button>
        ))}
      </div>
    )}
    {viavel === false && <p className="text-[10px] text-red-500 font-semibold inline-flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Tecnologia não vendável neste HP</p>}
  </div>
);

// Regra de crédito: aprovado abre todos os planos; reprovado abre só os grupos "NÃO APROVADOS NA POLÍTICA DE CRÉDITO";
// reprovado "análise N2 com fatura da concorrência" não abre nenhum plano (a Claro não libera produto → venda cancelada automaticamente)
export const liberadoPeloCredito = (p, credito) => {
  if (liberaPlanos(credito)) return true;
  if (ehAnaliseN2(credito)) return false;
  const g = (p.grupo || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();
  return /NAO\s+APROVAD/.test(g);
};

const CRED_CLS = {
  verde: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  ambar: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  vermelho: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};
const regraCredito = (credito, s) => {
  if (credito.aprovado) return 'todos os planos liberados';
  if (s.n2) return `${credito.mensagem} — nenhum plano liberado: ${AVISO_N2}`;
  return `${credito.mensagem ? `${credito.mensagem} — ` : ''}mostrando só os planos do grupo "NÃO APROVADOS NA POLÍTICA DE CRÉDITO"`;
};

const SeletorCredito = ({ valor, onChange, testid }) => {
  const [lista, setLista] = useState([]);
  useEffect(() => {
    let ativo = true;
    creditoAPI.historico({ limite: 15 }).then((r) => { if (ativo) setLista(r.data?.consultas || []); }).catch(() => {});
    return () => { ativo = false; };
  }, []);
  const opcoes = valor && !lista.some((c) => c.id === valor.id) ? [valor, ...lista] : lista;
  return (
    <select value={valor?.id || ''} onChange={(e) => onChange(opcoes.find((c) => c.id === e.target.value) || null)} className="h-7 max-w-[260px] rounded-md border bg-white dark:bg-gray-900 px-2 text-[11px]" data-testid={testid}>
      <option value="">Sem filtro de crédito (todos os planos)</option>
      {opcoes.map((c) => { const s = situacaoCredito(c.resultado, c.mensagem); return <option key={c.id} value={c.id}>{c.nome} — {s.curto || s.label} — {fmtDoc(c.cpf_cnpj)}</option>; })}
    </select>
  );
};

// Planos e preços da Claro na cidade (simulador do portal), filtrados pela tecnologia vendável no HP e pelo crédito do cliente
export const PlanosClaro = ({ uf, cidade, ibge, servicos, tipoCliente = 'RESIDENCIAL', credito: creditoProp = null, escolherCredito = false, onEscolherPlano = null, compacto = false, testid = 'planos-claro' }) => {
  const [tipo, setTipo] = useState(tipoCliente === 'EMPRESARIAL' ? 'EMPRESARIAL' : 'RESIDENCIAL');
  const [estado, setEstado] = useState({ carregando: false, res: null, chave: '' });
  const [aba, setAba] = useState('');
  const [todos, setTodos] = useState(false);
  const [creditoSel, setCreditoSel] = useState(creditoProp);
  useEffect(() => { setCreditoSel(creditoProp); }, [creditoProp]);
  const credito = creditoSel?.status === 'ok' ? creditoSel : null;
  const sCred = credito ? situacaoCredito(credito.resultado, credito.mensagem) : null;
  const chave = uf && cidade ? `${uf}|${cidade}|${tipo}` : '';

  const carregar = async (forcar = false) => {
    setEstado({ carregando: true, res: null, chave });
    try {
      const res = (await viabilidadeAPI.planos({ uf, cidade, ibge, tipo_cliente: tipo, forcar })).data;
      setEstado({ carregando: false, res, chave });
      if (res.status === 'ok' && res.tipos?.length) setAba((a) => (res.tipos.some((t) => t.codigo === a) ? a : res.tipos[0].codigo));
    } catch {
      setEstado({ carregando: false, res: { status: 'erro', mensagem: 'Falha ao consultar os planos' }, chave });
    }
  };
  useEffect(() => { if (chave) carregar(false); }, [chave]); // eslint-disable-line

  const tec = useMemo(() => tecnologiasDoHp(servicos), [servicos]);
  const viavel = (p) => (!tec || !p.tecnologia || !(p.tecnologia in tec) ? null : tec[p.tecnologia]);
  const r = estado.res;
  const passa = (p) => viavel(p) !== false && liberadoPeloCredito(p, credito);
  const lista = useMemo(() => {
    const t = r?.tipos?.find((x) => x.codigo === aba);
    if (!t) return [];
    return todos ? t.produtos : t.produtos.filter(passa);
  }, [r, aba, todos, tec, credito]); // eslint-disable-line
  const contagem = (t) => (todos ? t.produtos.length : t.produtos.filter(passa).length);

  if (!chave) return null;
  return (
    <div className="rounded-xl border bg-gray-50/70 dark:bg-gray-800/40 p-3 space-y-2" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold flex items-center gap-1.5"><Tag className="w-4 h-4 text-purple-600" /> Planos e preços da Claro em {cidade}/{uf}</p>
        <div className="flex items-center gap-1">
          <div className="inline-flex rounded-md overflow-hidden border text-[11px]" data-testid={`${testid}-tipo-cliente`}>
            {[['RESIDENCIAL', 'Residencial'], ['EMPRESARIAL', 'Empresarial']].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setTipo(v)} className={`px-2 py-1 font-semibold ${tipo === v ? 'bg-purple-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300'}`} data-testid={`${testid}-tipo-${v.toLowerCase()}`}>{l}</button>
            ))}
          </div>
          <button type="button" onClick={() => carregar(true)} disabled={estado.carregando} title="Atualizar na Claro" className="rounded-md border bg-white dark:bg-gray-900 p-1.5 hover:bg-gray-50 disabled:opacity-50" data-testid={`${testid}-atualizar`}>
            {estado.carregando ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
      {estado.carregando && <p className="text-xs text-gray-500 inline-flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Buscando o catálogo da cidade na Claro…</p>}
      {(escolherCredito || credito) && (
        <div className="flex flex-wrap items-center gap-2 text-[11px]" data-testid={`${testid}-credito`}>
          <span className="font-semibold text-gray-600 dark:text-gray-300 inline-flex items-center gap-1"><CreditCard className="w-3 h-3" /> Crédito do cliente:</span>
          {escolherCredito && <SeletorCredito valor={creditoSel} onChange={setCreditoSel} testid={`${testid}-credito-select`} />}
          {credito && (
            <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 font-bold ${CRED_CLS[sCred.tom]}`} data-testid={`${testid}-credito-badge`}>
              <sCred.Icone className="w-3 h-3" /> {sCred.label}
            </span>
          )}
          {credito && (
            <span className="text-gray-500" data-testid={`${testid}-credito-regra`}>
              {regraCredito(credito, sCred)}
            </span>
          )}
        </div>
      )}
      {r && r.status !== 'ok' && !estado.carregando && <ConectorAviso r={r} testid={`${testid}-aviso`} />}
      {r?.status === 'ok' && (
        <>
          <div className="flex flex-wrap items-center gap-1" data-testid={`${testid}-abas`}>
            {r.tipos.map((t) => {
              const Ic = ICONES[t.codigo] || Tag;
              return (
                <button key={t.codigo} type="button" onClick={() => setAba(t.codigo)} data-testid={`${testid}-aba-${t.codigo}`}
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold border ${aba === t.codigo ? 'bg-purple-600 border-purple-600 text-white' : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300'}`}>
                  <Ic className="w-3 h-3" /> {t.tipo} <span className="opacity-70">({contagem(t)}{contagem(t) !== t.produtos.length ? `/${t.produtos.length}` : ''})</span>
                </button>
              );
            })}
            {(tec || credito) && (
              <label className="ml-auto inline-flex items-center gap-1 text-[11px] text-gray-500 cursor-pointer">
                <input type="checkbox" checked={todos} onChange={(e) => setTodos(e.target.checked)} data-testid={`${testid}-todos`} /> mostrar todos (ignorar HP e crédito)
              </label>
            )}
          </div>
          {tec && <p className="text-[11px] text-gray-500">Vendável neste HP: {tec.HFC ? 'Cabo' : ''}{tec.HFC && tec.GPON ? ' e ' : ''}{tec.GPON ? 'Fibra' : ''}{!tec.HFC && !tec.GPON ? 'nenhuma tecnologia fixa (só Celular)' : ''}</p>}
          <div className={`grid gap-2 ${compacto ? 'sm:grid-cols-2' : 'sm:grid-cols-2 xl:grid-cols-3'}`} data-testid={`${testid}-lista`}>
            {lista.map((p) => <Produto key={`${p.id}-${p.tecnologia}`} p={p} viavel={viavel(p)} onEscolher={onEscolherPlano ? (prod, preco) => onEscolherPlano(prod, preco, aba) : null} />)}
            {lista.length === 0 && (
              <p className="text-xs text-gray-500 col-span-full" data-testid={`${testid}-vazio`}>
                {credito && ehAnaliseN2(credito) ? `Nenhum plano liberado — ${AVISO_N2}` : credito && !liberaPlanos(credito) ? 'Nenhum plano deste tipo liberado para crédito reprovado (marque "mostrar todos" para ver mesmo assim).' : 'Nenhum plano deste tipo vendável neste HP.'}
              </p>
            )}
          </div>
          {!compacto && r.destaques?.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer font-semibold text-gray-600 dark:text-gray-300">Combos em destaque ({r.destaques.filter((d) => !tec || !d.tecnologia || tec[d.tecnologia] !== false).length})</summary>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`${testid}-destaques`}>
                {r.destaques.filter((d) => !tec || !d.tecnologia || tec[d.tecnologia] !== false).map((d, i) => (
                  <span key={i} className="rounded-md border bg-white dark:bg-gray-900 px-2 py-1">
                    <b>{d.nome}</b>{TEC[d.tecnologia] ? ` (${TEC[d.tecnologia].label})` : ''}: {d.produtos.map((p) => p.nome).join(' + ')}
                  </span>
                ))}
              </div>
            </details>
          )}
          <p className="text-[10px] text-gray-400">{r.total_produtos} produto(s) • {r.cache ? 'catálogo em cache (até 12h)' : 'catálogo atualizado agora'} • preços do simulador da Conexão Claro; condições finais no portal</p>
        </>
      )}
    </div>
  );
};
